/**
 * Article template spacing collapse (G2 Task 17 fix round 1, DESIGN_REVIEW M-1).
 *
 * Sections are flex items of the article scroll column, and flex margins never collapse. The canvas and
 * TOKENS.md define each gap as a collapsed block-margin value: the gap between section A and the next
 * section B is max(A.spaceAfter, B.spaceBefore). A keeps its whole spaceAfter as margin-bottom and B's
 * margin-top becomes max(0px, B.spaceBefore − A.spaceAfter), all over the --ap-space-* variables, so the
 * phone values follow the style block's media query in the first paint (no JS).
 *
 * Rules:
 * - Only two token sides collapse. A literal margin side (layout.margin.top/bottom) wins (D4): it is
 *   emitted verbatim by SectionWrapper and never collapses, so the neighbour's token side stays whole.
 * - A hideable section (ArticleRelated, ArticleIndex: the style block hides its wrapper with display:none while the
 *   region is empty, M8, and G3 can fill or empty the region after publish) is transparent: the section
 *   after it collapses against the section before it, and the hideable section's own margin-bottom takes
 *   the difference, so every gap is right whether the region is shown or hidden. Limitation: one hideable
 *   section between two others (the template has one related region).
 * - The first section keeps its whole spaceBefore; the last keeps its whole spaceAfter.
 */
import type { SectionLayout } from '@segmently/schema-models/v2';
import { hasOwn } from '@modules/screen-view-v2/utils/articlePresentationCss';

/** The token-derived margins of one section wrapper; an absent side is not emitted. */
export interface ArticleSectionSpacing {
  marginTop?: string;
  marginBottom?: string;
}

export interface ArticleSpacingSection {
  id: string;
  kind: string;
  layout?: SectionLayout;
}

/**
 * Kinds whose wrapper the style block can hide (M8: `[data-section-id]:has(>aside[data-seg-related][hidden])`;
 * ArticleIndex: `[data-section-id]:has(>section[data-seg-index][hidden])`).
 */
const HIDEABLE_KINDS: ReadonlySet<string> = new Set(['ArticleRelated', 'ArticleIndex']);

type Side = { kind: 'none' } | { kind: 'literal' } | { kind: 'token'; css: string };

function side(layout: SectionLayout | undefined, edge: 'top' | 'bottom', spacing: object): Side {
  if (layout?.margin?.[edge] !== undefined) return { kind: 'literal' };
  const token = edge === 'top' ? layout?.spaceBefore : layout?.spaceAfter;
  return token && hasOwn(spacing, token) ? { kind: 'token', css: `var(--ap-space-${token})` } : { kind: 'none' };
}

/** B's margin-top after A: only a token spaceBefore is emitted here; it subtracts A's token spaceAfter. */
function collapsedTop(previousAfter: Side, before: Side): string | undefined {
  if (before.kind !== 'token') return undefined;
  return previousAfter.kind === 'token' ? `max(0px, calc(${before.css} - ${previousAfter.css}))` : before.css;
}

const NONE: Side = { kind: 'none' };

/**
 * The token margins of every section, by id, for the sections in render order (only the ones that
 * render a wrapper). Every section gets an entry; an absent side emits nothing (none, or a literal).
 */
export function collapseArticleSpacing(
  sections: ReadonlyArray<ArticleSpacingSection>,
  spacing: object,
): Map<string, ArticleSectionSpacing> {
  const sides = sections.map((section) => ({
    top: side(section.layout, 'top', spacing),
    bottom: side(section.layout, 'bottom', spacing),
  }));
  const result = new Map<string, ArticleSectionSpacing>();
  // The spaceAfter the next section collapses against: that of the last section that is not transparent.
  let anchorAfter: Side = NONE;
  sections.forEach((section, i) => {
    const { top, bottom } = sides[i];
    const transparent = HIDEABLE_KINDS.has(section.kind) && bottom.kind !== 'literal';
    const out: ArticleSectionSpacing = {};
    const marginTop = collapsedTop(anchorAfter, top);
    if (marginTop !== undefined) out.marginTop = marginTop;
    let marginBottom = bottom.kind === 'token' ? bottom.css : undefined;
    if (transparent && i + 1 < sections.length) {
      // The next section collapses against anchorAfter (right while this one is hidden); when this one is
      // shown, its margin-bottom adds back what the next section would have collapsed against it instead.
      const shown = collapsedTop(bottom, sides[i + 1].top);
      const skipped = collapsedTop(anchorAfter, sides[i + 1].top);
      if (shown !== undefined && skipped !== undefined && shown !== skipped) {
        marginBottom = `calc(${marginBottom !== undefined ? `${marginBottom} + ` : ''}${shown} - ${skipped})`;
      }
    }
    if (marginBottom !== undefined) out.marginBottom = marginBottom;
    result.set(section.id, out);
    if (!transparent) anchorAfter = bottom;
  });
  return result;
}
